import * as React from 'react';
import * as styles from "./Panel.css";
import { inject, observer } from 'mobx-react';
import { MessagesStore } from '../stores';
import { IAPanel } from '../../panels/Panel';
import { IAFullscreenPanel } from '../../panels/FullscreenPanel';
import DateTimeService from '../../../services/DateTimeService';
import { IATooltip } from '../../tooltip/Tooltip';
import IAAuthors from '../../panels/authors/IAAuthors';
import { LocalizationService } from '../../../services/LocalizationService';
import { Helper } from '../../../Helper';
import { ConditionalOutputFactory } from '../../../services/ConditionalOutputFactory';
import { Environment } from '../../../services/Environment';
import * as _html2canvas from "html2canvas";
import jsPDF from 'jspdf';
import { LoggingService } from '../../../services/LoggingService';

export interface IViewPanelProps {
  id: number;
  messagesStore?: MessagesStore;
  isContentCreator?: boolean;
  dataAutomationIdPrefix?: string;
  top?: number;
  enablePrint?: boolean; // overrides instance setting
  onClose?: () => void;
  onEdit?: () => void;
  getNextPage?: () => Promise<void>;
}

export interface IViewPanelState {
  printing?: boolean;
}

@inject('messagesStore')
@observer
export class OldViewPanel extends React.Component<IViewPanelProps, IViewPanelState> {

  private readonly dateTimeService: DateTimeService = new DateTimeService();
  private printPanelId: string;

  constructor(props: IViewPanelProps) {
    super(props);
    this.state = { printing: false };
    this.printPanelId = Helper.getRandomStringKey();
  }

  public componentWillReceiveProps(props: IViewPanelProps): void {
    if (props.id != undefined && props.id !== this.props.id) {
      this.props.messagesStore.getInstance();
      if (LoggingService !== undefined) {
        LoggingService.trackEvent(this.props.messagesStore.component === "Messages" ? "OpenMessage" : "OpenEvent", {
          ActionType: this.props.messagesStore.component === "Messages" ? "OpenMessage" : "OpenEvent",
          Component: this.props.messagesStore.component,
          Id: this.props.messagesStore.messageToOpen.id,
          Title: this.props.messagesStore.messageToOpen.title,
          Categories: this.props.messagesStore.messageToOpen.categories,
          StartDate: this.props.messagesStore.messageToOpen.startDate,
          Created: this.props.messagesStore.messageToOpen.created,
          Author: this.props.messagesStore.messageToOpen.author,
          Tenant: this.props.messagesStore.tenant,
          Instance: this.props.messagesStore.instance?.instanceName,
          UserId: this.props.messagesStore.token,
          Environment: this.props.messagesStore.environment
        });
      }
    }
  }

  private emailLink(): void {
    const url = window.location.href.split('?')[0];
    let linkToMessage: string;
    if (this.props.messagesStore.component === "Messages") {
      if (this.props.messagesStore.instance?.settings?.openMessageLink) {
        linkToMessage = `${Environment.getEnvironmentForLinkPortal(this.props.messagesStore.environment)}/${this.props.messagesStore.tenant}/${this.props.messagesStore.instance.instanceName}/${this.props.messagesStore.messageToOpen.id}`;
      } else {
        linkToMessage = `${url}?instance=${this.props.messagesStore.instance.instanceName}&messageId=${this.props.messagesStore.messageToOpen.id}`;
      }
    } else {
      linkToMessage = `${url}?instance=${this.props.messagesStore.instance.instanceName}&eventId=${this.props.messagesStore.messageToOpen.id}`;
    }
    var link = "mailto:"
      + "?subject=" + encodeURIComponent(this.props.messagesStore.messageToOpen.title)
      + "&body=" + encodeURIComponent(linkToMessage);
    window.location.href = link;
  }

  private enableNext(): boolean {
    if (this.props.messagesStore.messageToOpen) {
      const disabled = (
        Math.ceil(this.props.messagesStore.messages.length / this.props.messagesStore.numMessages) === this.props.messagesStore.pageNumber &&
        this.props.messagesStore.messages.length <= this.props.messagesStore.messageIndex);
      return !disabled;
    }
  }

  private nextMessage(): void {
    this.props.messagesStore.pageNumber = Math.ceil((this.props.messagesStore.messageIndex + 1) / this.props.messagesStore.numMessages);
    if (this.props.messagesStore.pageNumber < 1) {
      this.props.messagesStore.pageNumber = 1;
    }
    const nextMessageIsOnPage = Math.ceil((this.props.messagesStore.messageIndex + 2) / this.props.messagesStore.numMessages);
    if (nextMessageIsOnPage !== this.props.messagesStore.pageNumber) {
      if (this.props.messagesStore.pageNumber * this.props.messagesStore.numMessages < this.props.messagesStore.messages.length) {
        this.props.messagesStore.pageNumber++;
        this.props.messagesStore.openNextMessage();
      } else {
        this.props.messagesStore.pageNumber++;
        this.props.getNextPage();
      }
    } else {
      this.props.messagesStore.openNextMessage();
    }
  }

  private enablePrevious(): boolean {
    if (this.props.messagesStore.messageToOpen) {
      return this.props.messagesStore.messageIndex > 0;
    }
  }

  private previousMessage(): void {
    this.props.messagesStore.pageNumber = Math.ceil((this.props.messagesStore.messageIndex + 1) / this.props.messagesStore.numMessages);
    if (this.props.messagesStore.pageNumber < 1) {
      this.props.messagesStore.pageNumber = 1;
    }
    const previousMessageIsOnPage = Math.ceil((this.props.messagesStore.messageIndex) / this.props.messagesStore.numMessages);
    if (previousMessageIsOnPage !== this.props.messagesStore.pageNumber) {
      if (this.props.messagesStore.pageNumber != 1) {
        this.props.messagesStore.pageNumber--;
        this.props.messagesStore.openPreviousMessage()
      }
    } else {
      this.props.messagesStore.openPreviousMessage()
    }
  }

  private printMessage(): void {
    this.setState({ printing: true }, () => {
      const html2canvas: any = (_html2canvas as any).default ?? _html2canvas;

      var panel: HTMLElement;
      var maxWidth: number;

      if (this.props.messagesStore.isFullPageView) {
        panel = document.getElementById(`fullscreenPanelContentArea-${this.printPanelId}`);
        maxWidth = 150;
      } else {
        panel = document.getElementById(`slimPanelContentArea-${this.printPanelId}`);
        maxWidth = 120;
      }

      const width = panel.scrollWidth; // width in pixels
      const height = panel.scrollHeight; // height in pixels
      const sizeScale = height / width; // scale between height and width - used for resizing later

      var printHeight = 297 - 10 - 10; // height of an A4 (mm) - 10 mm margin in top and bottom
      const heightScale = height / printHeight; // scale between height in mm and height in pixels
      var printWidth = width / heightScale; // convert pixel width to mm by using the height scale
      var marginLeft = (210 - printWidth) / 2; // calculate the left margin to center the print

      if (printWidth > maxWidth) {
        // if the message is short, the width of the content will be high
        // we need to "zoom" out on short messages to avoid too much pixelation
        const difference = printWidth - maxWidth; // difference between max width and actual width
        printHeight -= difference * sizeScale; // subtracts the difference in width correctly scaled
        printWidth = maxWidth; // sets the width to the max width
        marginLeft += difference / 2; // adds the remaining difference to the margin
      }

      html2canvas(panel, {
        allowTaint: false,
        useCORS: true,
        height: height,
        windowHeight: height,
      }).then((canvas: HTMLCanvasElement) => {
        this.setState({ printing: false });
        const imgData = canvas.toDataURL('image/jpeg');
        const pdf = new jsPDF();

        pdf.addImage(imgData, 'JPEG', marginLeft, 10, printWidth, printHeight);

        // open pdf in new window
        const newTab = window.open(pdf.output('bloburl').toString(), '_blank');
        newTab.addEventListener('load', () => {
          // not working on firefox
          // https://bugzilla.mozilla.org/show_bug.cgi?id=1251028
          newTab.print();
        });
      }).catch(() => this.setState({ printing: false }));
    });
  }

  public render(): JSX.Element {
    let openInNewViewer = this.props.messagesStore.instance && this.props.messagesStore.messageToOpen && this.props.messagesStore.messageToOpen.iaConfiguration && this.props.messagesStore.messageToOpen.iaConfiguration.composerComponents;
    let headerImage = this.props.messagesStore.messageToOpen != undefined ? this.props.messagesStore.messageToOpen.image : undefined;
    const shouldUpdateLocalCache = this.props.messagesStore.tenantSettings && this.props.messagesStore.tenantSettings.image && this.props.messagesStore.tenantSettings.image.shouldUpdateLocalCache;
    if (headerImage != null) {
      if (shouldUpdateLocalCache) {
        headerImage = headerImage.replace("DefaultRendition", "StandardLarge").replace("version=&", `version=${this.props.messagesStore.messageToOpen.modified ? (new Date(this.props.messagesStore.messageToOpen.modified)).toISOString().replace(/[^\d]/g, '') : ""}&ignoreCache=false&`);
      } else {
        headerImage = headerImage.replace("DefaultRendition", "StandardLarge");
      }
    }
    let date: string;
    if (this.props.messagesStore.messageToOpen != undefined && this.props.messagesStore.messageToOpen.startDate != undefined) {
      date = this.dateTimeService.ConvertToDDMMYYYY(this.props.messagesStore.messageToOpen.startDate) === this.dateTimeService.ConvertToDDMMYYYY(new Date()) ? new LocalizationService().strings.Messages_ViewPanel_Today + " " + this.dateTimeService.getHoursLocalized(this.props.messagesStore.messageToOpen.startDate) : this.dateTimeService.getFullDateLocalized(this.props.messagesStore.messageToOpen.startDate);
    }

    let content;
    let contentParse;

    let top = 0;
    if (Helper.getDisplayEnvironment() === "Sharepoint" || Helper.getDisplayEnvironment() === "SharepointClassic") {
      top = 48;
    }
    if (this.props.messagesStore.isMobileApp) {
      top = window.platform === "android" ? 50 : Helper.isIPhoneXType() ? 90 : 70;
    }
    if (this.props.messagesStore.messageToOpen != undefined) {
      if (this.props.messagesStore.messageToOpen.content) {
        let contentDiv = document.createElement('div');
        contentDiv.innerHTML = this.props.messagesStore.contentTranslated ? this.props.messagesStore.contentTranslated : this.props.messagesStore.messageToOpen.content;
        let links = contentDiv.getElementsByTagName("a");
        if (links && links.length > 0) {
          for (let i = 0; i < links.length; i++) {
            let link = links[i];
            link.setAttribute("data-interception", "off");
            link.style.color = this.props.messagesStore.highlightColor;
          }
        }
        let images = contentDiv.getElementsByTagName("img");
        if (images && images.length > 0) {
          for (let i = 0; i < images.length; i++) {
            let img: HTMLImageElement | JSX.Element = images[i];
            let imgCropSettings = this.props.messagesStore.messageToOpen.iaConfiguration ? this.props.messagesStore.messageToOpen.iaConfiguration.contentImgCrop ? this.props.messagesStore.messageToOpen.iaConfiguration.contentImgCrop[img.src] : undefined : undefined;

            let imgAlt
            if (img.src.includes("path=")) {
              let decodedURL = decodeURIComponent(img.src.split("path=")[1]);
              if (this.props.messagesStore.messageToOpen?.iaConfiguration?.contentImgAlt && this.props.messagesStore.messageToOpen?.iaConfiguration?.contentImgAlt[decodedURL]) {
                imgAlt = this.props.messagesStore.messageToOpen.iaConfiguration.contentImgAlt[decodedURL];
              } else {
                imgAlt = "";
              }
            } else {
              if (this.props.messagesStore.messageToOpen?.iaConfiguration?.contentImgAlt && this.props.messagesStore.messageToOpen?.iaConfiguration?.contentImgAlt[img.src]) {
                imgAlt = this.props.messagesStore.messageToOpen.iaConfiguration.contentImgAlt[img.src];
              } else {
                imgAlt = "";
              }
            }
            if (Helper.isValidUrl(img.src)) {
              let imgURL = Helper.getImageUrl(Environment.getEnvironmentForImageAPI(this.props.messagesStore.environment), this.props.messagesStore.tenant, this.props.messagesStore.token, img.src, undefined, "Full", imgCropSettings);
              img.src = imgURL;
            }
            img.alt = imgAlt;
          }
        }
        contentParse = contentDiv.innerHTML;
      }

      let categoriesText = undefined;
      let listOfCategories = [];
      if (this.props.messagesStore.messageToOpen.categories && this.props.messagesStore.messageToOpen.categories.length > 0) {
        this.props.messagesStore.messageToOpen.categories.forEach((categoryElement: any) => {
          listOfCategories.push(
            <div key={categoryElement.id}>{categoryElement.name}</div>
          );
        });
        categoriesText = this.props.messagesStore.messageToOpen.categories.slice(0, 1).reduce((x, y) => { return x + (x ? ", " : "") + y.name }, '')
          + (this.props.messagesStore.messageToOpen.categories.length > 1 ? ` +${this.props.messagesStore.messageToOpen.categories.length - 1}` : '');
      }
      let authors = [];
      if (this.props.messagesStore.messageToOpen != undefined) {
        if (this.props.messagesStore.messageToOpen.contacts && this.props.messagesStore.messageToOpen.contacts.length > 0) {
          authors = this.props.messagesStore.messageToOpen.contacts;
        }
        if (authors.length == 0 && this.props.messagesStore.messageToOpen.author != undefined) {
          authors.push(this.props.messagesStore.messageToOpen.author);
        }
      }
      content = (
        <div className={styles.IA_viewPanelContentWrapper}>
          <div className={styles.IA_viewPanelContent}>
            {this.props.messagesStore.messageToOpen.categories != undefined && this.props.messagesStore.messageToOpen.categories.length > 0 && this.props.messagesStore.messageToOpen.categories.length > 1 &&
              <IATooltip
                content="Tooltip"
                type={this.props.messagesStore.isMobile ? "popup" : "bubble"}
                styles={{
                  width: 200,
                  textAlign: "left",
                  color: "#333333",
                }}
                componentContent={
                  <div>
                    {listOfCategories}
                  </div>
                }
              >
                <div
                  className={styles.IA_categories}
                  style={{
                    cursor: "pointer",
                    color: this.props.messagesStore.highlightColor
                  }}
                >
                  {categoriesText}
                </div>
              </IATooltip>
            }
            {this.props.messagesStore.messageToOpen.categories != undefined && this.props.messagesStore.messageToOpen.categories.length > 0 && this.props.messagesStore.messageToOpen.categories.length == 1 &&
              <div
                className={styles.IA_categories}
                style={{
                  cursor: "default",
                  color: "#333333"
                }}
              >
                {categoriesText}
              </div>
            }
            <div className={styles.title}>
              <h1>{this.props.messagesStore.titleTranslated ? this.props.messagesStore.titleTranslated : this.props.messagesStore.messageToOpen.title}</h1>
            </div>
            {date &&
              <div
                style={{
                  float: "left",
                  marginBottom: 15,
                  fontSize: 13,
                  color: "#858585"
                }}
              >
                {date}
              </div>
            }
            {authors != undefined && this.props.messagesStore.component === "Messages" &&
              <IAAuthors
                highlightColor={this.props.messagesStore.highlightColor}
                authors={authors}
                style={{
                  clear: "both",
                  padding: 0,
                  overflow: "hidden"
                }}
              />
            }
            {this.props.messagesStore.component === "Event" &&
              <div className={styles.IA_time}>
                <div className={styles.IA_propertyValue}>{this.dateTimeService.getEventString(this.props.messagesStore.messageToOpen.startDate, this.props.messagesStore.messageToOpen.endDate, this.props.messagesStore.messageToOpen.allDayEvent)}</div>
              </div>
            }
            {this.props.messagesStore.component === "Event" && this.props.messagesStore.messageToOpen && this.props.messagesStore.messageToOpen.location &&
              <div className={styles.IA_locataion}>
                <div className={styles.IA_propertyValue}>{this.props.messagesStore.messageToOpen.location}</div>
              </div>
            }
            <div
              style={{
                clear: "both",
                marginTop: 10
              }}
              dangerouslySetInnerHTML={{ __html: contentParse }}
            />
            {
              (this.props.isContentCreator || this.props.messagesStore.isAdminPage) && <div className={styles.modifiedBy}>{new LocalizationService().strings.Messages_ViewPanel_ModifiedBy} {this.props.messagesStore.messageToOpen.editor != undefined ? this.props.messagesStore.messageToOpen.editor.displayName : (this.props.messagesStore.messageToOpen.author ? this.props.messagesStore.messageToOpen.author.displayName : "-")} - {this.dateTimeService.ConvertToDDMMYYYY(this.props.messagesStore.messageToOpen.modified) == this.dateTimeService.ConvertToDDMMYYYY(new Date()) ? new LocalizationService().strings.DateTimeService_Today + " " + this.dateTimeService.getHoursLocalized(this.props.messagesStore.messageToOpen.modified) : this.dateTimeService.getFullDateLocalized(this.props.messagesStore.messageToOpen.modified)}</div>
            }
          </div>
        </div>
      );
    }
    if (this.props.messagesStore.isFullPageView && !this.props.messagesStore.isMobile) {
      return (
        <IAFullscreenPanel
          approvers={this.props.messagesStore.messageToEdit?.approvers}
          showUniqueViews={this.props.messagesStore.showUniqueViews}
          uniqueViewsCount={this.props.messagesStore.messageToOpen?.uniqueViews}
          headerImage={headerImage}
          headerVideo={this.props.messagesStore.messageToOpen != undefined ? this.props.messagesStore.messageToOpen.videoUrl : undefined}
          iaConfiguration={this.props.messagesStore.messageToOpen?.iaConfiguration}
          date={date}
          width={document.getElementById("IA_navigator_vertical_menu") != undefined ? window.innerWidth - 50 : window.innerWidth}
          open={this.props.messagesStore.messageToOpen != undefined && this.props.id != undefined}
          transition={this.props.messagesStore.panelIsOpen ? "fade" : "slide"}
          highlightColor={this.props.messagesStore.highlightColor}
          marginTop={this.props.top ? this.props.top : top}
          isMobile={this.props.messagesStore.isMobile}
          user={this.props.messagesStore.user}
          itemId={this.props.messagesStore.messageToOpen != undefined && this.props.messagesStore.messageToOpen.id != undefined ? this.props.messagesStore.messageToOpen.id.toString() : undefined}
          tenant={this.props.messagesStore.tenant}
          component={this.props.messagesStore.component}
          instance={this.props.messagesStore.instance}
          token={this.props.messagesStore.token}
          environment={this.props.messagesStore.environment}
          source={this.props.messagesStore.source}
          close={() => {
            if (this.props.messagesStore && this.props.messagesStore.messageToOpen !== undefined) {
              if (this.props.isContentCreator) {
                this.props.onClose();
              } else {
                this.props.messagesStore.clearTranslation();
                this.props.messagesStore.closeMessage();
              }
            }
          }}
          enableNext={this.enableNext()}
          enablePrevious={this.enablePrevious()}
          onEdit={this.props.messagesStore.userRole == "Administrator" || this.props.messagesStore.userRole == "Editor" && this.props.messagesStore.messageToOpen != undefined && this.props.messagesStore.messageToOpen.author != undefined && this.props.messagesStore.user != undefined && this.props.messagesStore.messageToOpen.author.email?.toLowerCase() == this.props.messagesStore.user.email?.toLowerCase() ? () => {
            this.props.messagesStore.editMessage(this.props.messagesStore.messageToOpen);
          } : undefined}
          previousMessage={this.props.messagesStore.openedFromLink ? undefined : () => this.previousMessage()}
          nextMessage={this.props.messagesStore.openedFromLink ? undefined : () => this.nextMessage()}
          onCopyLink={this.props.messagesStore.webpartId == undefined ? undefined : () => {
          }}
          onEmailLink={this.props.messagesStore.webpartId == undefined || this.props.messagesStore.isMobileApp ? undefined : () => this.emailLink()}
          // Likes and comments
          likesEnabled={this.props.messagesStore.likesEnabled && !this.props.messagesStore.messageToOpen?.iaConfiguration?.disableLikes}
          commentsEnabled={this.props.messagesStore.commentsEnabled && !this.props.messagesStore.messageToOpen?.iaConfiguration?.disableComments}
          likesOnCommentsEnabled={this.props.messagesStore.commentsEnabled}
          likes={this.props.messagesStore.messageToOpen != undefined ? this.props.messagesStore.messageToOpen.likes : undefined}
          commentCount={this.props.messagesStore.messageToOpen != undefined ? this.props.messagesStore.messageToOpen.commentCount : undefined}
          addComment={this.props.messagesStore.addComment}
          likesUpdated={(likes: any[]) => {
            this.props.messagesStore.likesUpdated(likes);
            this.forceUpdate();
          }}
          commentsUpdated={(comments: number) => {
            this.props.messagesStore.commentsUpdated(comments);
            this.forceUpdate();
          }}
          commentClicked={() => {
            this.props.messagesStore.updateEditorState();
          }}
          commentCanceled={() => {
            this.props.messagesStore.updateEditorState();
          }}
          // Translation
          onTranslate={(language: string) => this.props.messagesStore.getTranslation(language, this.props.messagesStore.messageToOpen.title, "", this.props.messagesStore.messageToOpen.content, this.props.messagesStore.messageToOpen != undefined && this.props.messagesStore.messageToOpen.categories != undefined ? this.props.messagesStore.messageToOpen.categories.reduce((x, y) => { return x + (x ? ", " : "") + y.name }, 'none') : undefined)}
          enableTranslation={this.props.messagesStore.instance?.translationEnabled}
          isTranslating={this.props.messagesStore.isTranslating}
          dataAutomationIdPrefix={this.props.dataAutomationIdPrefix}
          enablePrint={this.props.enablePrint ?? (this.props.messagesStore.instance?.settings && this.props.messagesStore.instance?.settings.enablePrinting)}
          panelId={this.printPanelId}
          printing={this.state.printing}
          printMessage={() => this.printMessage()}
        >{!openInNewViewer ? content : <></>}</IAFullscreenPanel>
      );
    } else {
      let itemId: string = undefined;
      if (this.props.messagesStore.messageToOpen?.id !== undefined) {
        if (!this.props.messagesStore.instance?.approvalEnabled || (this.props.messagesStore.instance?.approvalEnabled && this.props.messagesStore.messageToOpen?.approvalStatus === "Approved")) {
          itemId = this.props.messagesStore.messageToOpen.id.toString();
        }
      }
      return <IAPanel
        showUniqueViews={this.props.messagesStore.showUniqueViews}
        uniqueViewsCount={this.props.messagesStore.messageToOpen?.uniqueViews}
        showNavigationBar={true}
        headerImage={headerImage}
        headerVideo={this.props.messagesStore.messageToOpen != undefined ? this.props.messagesStore.messageToOpen.videoUrl : undefined}
        iaConfiguration={this.props.messagesStore.messageToOpen?.iaConfiguration}
        date={date}
        isMobile={this.props.messagesStore.isMobile}
        open={this.props.messagesStore.messageToOpen != undefined && !this.props.messagesStore.messageToOpen?.iaConfiguration?.composerComponents && this.props.id != undefined}
        transition={this.props.messagesStore.panelIsOpen ? "fade" : "slide"}
        width={this.props.messagesStore.isMobile ? window.innerWidth : 700}
        highlightColor={this.props.messagesStore.highlightColor}
        marginTop={this.props.top ? this.props.top : top}
        likesEnabled={this.props.messagesStore.likesEnabled && !this.props.messagesStore.messageToOpen?.iaConfiguration?.disableLikes}
        commentsEnabled={this.props.messagesStore.commentsEnabled && !this.props.messagesStore.messageToOpen?.iaConfiguration?.disableComments}
        likesOnCommentsEnabled={this.props.messagesStore.commentsEnabled}
        user={this.props.messagesStore.user}
        itemId={itemId}
        tenant={this.props.messagesStore.tenant}
        component={this.props.messagesStore.component}
        instance={this.props.messagesStore.instance}
        token={this.props.messagesStore.token}
        environment={this.props.messagesStore.environment}
        source={this.props.messagesStore.source}
        mobileCloseButton={true}
        webpartId={this.props.messagesStore.webpartId}
        close={() => {
          if (this.props.messagesStore && this.props.messagesStore.messageToOpen !== undefined) {
            if (this.props.isContentCreator) {
              this.props.onClose();
            } else {
              this.props.messagesStore.clearTranslation();
              this.props.messagesStore.closeMessage();
            }
          }
        }}
        onEdit={this.props.isContentCreator ?
          () => {
            this.props.onEdit()
          }
          :
          ConditionalOutputFactory
            .WithDefault<() => void>(undefined)
            .OnCondition(this.props.messagesStore.userRole == "Administrator", () => this.onIAPanelEditClicked())
            .OnCondition(this.props.messagesStore.userRole == "Editor"
              && this.props.messagesStore.messageToOpen?.author?.email?.toLowerCase() == this.props.messagesStore.user?.email?.toLowerCase(), () => this.onIAPanelEditClicked())
            .OnCondition(this.props.messagesStore.userRole == "Editor"
              && this.props.messagesStore.messageToOpen?.editor?.email?.toLowerCase() == this.props.messagesStore.user?.email?.toLowerCase(), () => this.onIAPanelEditClicked())
            .Last()
        }
        enableNext={this.enableNext()}
        enablePrevious={this.enablePrevious()}
        loading={this.props.messagesStore.panelContentIsLoading}
        previousMessage={this.props.messagesStore.openedFromLink ? undefined : () => this.previousMessage()}
        nextMessage={this.props.messagesStore.openedFromLink ? undefined : () => this.nextMessage()}
        likes={this.props.messagesStore.messageToOpen != undefined ? this.props.messagesStore.messageToOpen.likes : undefined}
        commentCount={this.props.messagesStore.messageToOpen != undefined ? this.props.messagesStore.messageToOpen.commentCount : undefined}
        likesUpdated={(likes: any[]) => {
          this.props.messagesStore.likesUpdated(likes);
          this.forceUpdate();
        }}
        commentsUpdated={(comments: number) => {
          this.props.messagesStore.commentsUpdated(comments);
          this.forceUpdate();
        }}
        addComment={this.props.messagesStore.addComment}
        commentClicked={
          () => {
            this.props.messagesStore.updateEditorState();
          }
        }
        onCopyLink={
          this.props.messagesStore.webpartId || this.props.messagesStore.isNavigator ? () => {
            // this.props.messagesStore.showNotification(
            //   "success",
            //   "The link has been copied to the clipboard!",
            //   "Emoji"
            // );
          } : undefined
        }
        commentCanceled={
          () => {
            this.props.messagesStore.updateEditorState();
          }
        }
        onEmailLink={(this.props.messagesStore.webpartId && !this.props.messagesStore.isMobileApp) || this.props.messagesStore.isNavigator ? () => this.emailLink() : undefined}
        onExportToCalender={this.props.messagesStore.component === "Event" && this.props.messagesStore.messageToOpen ? `${Environment.getEnvironmentForEventsAPI(this.props.messagesStore.environment)}/${this.props.messagesStore.tenant.replace('.sharepoint.com', '')}/${this.props.messagesStore.instance?.instanceName}/event/${this.props.messagesStore.messageToOpen.id}/export?token=${this.props.messagesStore.token}` : undefined}
        onTranslate={(language: string) => {
          this.props.messagesStore.getTranslation(language, this.props.messagesStore.messageToOpen.title, "", this.props.messagesStore.messageToOpen.content, this.props.messagesStore.messageToOpen != undefined && this.props.messagesStore.messageToOpen.categories != undefined ? this.props.messagesStore.messageToOpen.categories.reduce((x, y) => { return x + (x ? ", " : "") + y.name }, 'none') : undefined);
        }}
        enableTranslation={this.props.messagesStore.instance?.translationEnabled}
        isTranslating={this.props.messagesStore.isTranslating}
        dataAutomationIdPrefix={this.props.dataAutomationIdPrefix}
        enablePrint={this.props.enablePrint ?? (this.props.messagesStore.instance?.settings && this.props.messagesStore.instance?.settings.enablePrinting)}
        panelId={this.printPanelId}
        printing={this.state.printing}
        printMessage={() => this.printMessage()}
        isContentCreator={this.props.isContentCreator}
        isOldPanel={true}
      >
        {this.props.id && !openInNewViewer ? content : <></>}
      </IAPanel >;
    }
  }



  private onIAPanelEditClicked() {
    this.props.messagesStore.showConvertToComposerPopup = this.props.messagesStore.messageToOpen;
  }
}